import type {
	CustomerCarrier,
	GlobalCarrier,
} from "~/shared/schemas/auto-dispatch-schema.ts";

import { AutoDispatchFilterCell } from "../../components/grid/columns/auto-dispatch-filter-cell/index.tsx";
import { SwitchCell } from "../../components/grid/columns/switch-cell/index.tsx";
import type { column } from "./types.ts";
import type { CellWrapper } from "./with-loader.tsx";

type AutoDispatchHandlerParams = {
	isCustomerGrid: boolean;
	updateCarrier?: (carrier: GlobalCarrier) => void;
	updateCustomerCarrier?: (carrier: CustomerCarrier) => Promise<void>;
	showError: (message: string) => void;
};

const getCarrierAriaLabel = (carrier: GlobalCarrier) =>
	`Auto Dispatch for ${carrier.carrierName ?? "carrier " + carrier.carrierId}`;

export const makeHandleAutoDispatchChange =
	({
		isCustomerGrid,
		updateCarrier,
		updateCustomerCarrier,
		showError,
	}: AutoDispatchHandlerParams) =>
	(
		dataItem: GlobalCarrier | CustomerCarrier,
		value: boolean,
	): false | void | Promise<void> => {
		if (isCustomerGrid && updateCustomerCarrier) {
			return updateCustomerCarrier({
				...(dataItem as CustomerCarrier),
				canAutoDispatch: value,
			});
		}

		if (value) {
			const carrier = dataItem as GlobalCarrier;
			const missingFields = [];
			if (!carrier.dispatchDaysInAdvance) {
				missingFields.push("Call Timing Day");
			}
			if (!carrier.dispatchTime) {
				missingFields.push("Call Timing Hours");
			}

			if (missingFields.length > 0) {
				showError(
					`Cannot enable Auto Dispatch: Missing ${missingFields.join(", ")}.`,
				);
				return false;
			}
		}

		updateCarrier?.({
			...(dataItem as GlobalCarrier),
			canAutoDispatch: value,
		});
	};

export const autoDispatchColumn = (
	cellWrapper: CellWrapper,
	onAutoDispatchChange: (
		dataItem: GlobalCarrier | CustomerCarrier,
		value: boolean,
	) => false | void | Promise<void>,
): column => ({
	field: "canAutoDispatch",
	title: "Auto Dispatch",
	group: ["global", "customer"],
	filter: "boolean",
	filterable: true,
	filterCell: AutoDispatchFilterCell,
	sortable: false,
	width: 120,
	cells: {
		data: cellWrapper((props) => (
			<SwitchCell
				{...props}
				ariaLabel={getCarrierAriaLabel(props.dataItem as GlobalCarrier)}
				checked={(props.dataItem as GlobalCarrier)?.canAutoDispatch}
				data={props.dataItem as GlobalCarrier}
				dataTestId={`carrier-auto-dispatch-switch-${(props.dataItem as GlobalCarrier).carrierId}`}
				onChange={(value: boolean) =>
					onAutoDispatchChange(
						props.dataItem as GlobalCarrier | CustomerCarrier,
						value,
					)
				}
			/>
		)),
	},
});
